import { MediaMatcher } from '@angular/cdk/layout';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { FacilityInformationType } from '@cargos/sprintpay-services';
import { mergeDeep } from '@cargos/sprintpay-tools';
import { from, take } from 'rxjs';
import { GoogleMapService } from 'src/app/shared/services/google-map.service';
import { WebSite } from '../../models/facility-information';
import { CurrentFacilityService } from '../../services';
import { CustomerServiceInfo } from '../../services/current-facility.service';
import { AdvancedMarkerElementOptionsCustom } from './google-map/models/advanced-marker-element-options';

@Component({
    selector: 'app-sprintpay-facility-information',
    templateUrl: './facility-information.component.html',
    styleUrls: ['./facility-information.component.scss'],
    standalone: false,
})
export class FacilityInfComponent implements OnDestroy, OnInit {
    private _mobileQueryListener: () => void;
    public facilityInformation: FacilityInformationType[] = [];
    public mobileQuery: MediaQueryList;
    public mapOptions: google.maps.MapOptions;
    public markers: AdvancedMarkerElementOptionsCustom[] = [];
    public urlWebSite: WebSite;
    public isMapEnable = false;
    public indexExpanded: number;
    public customerServiceInfo: CustomerServiceInfo | null = null;

    @Input() isLoading?: boolean;
    @Input() zoom: number = 6;
    @Input()
    set data(data: FacilityInformationType[]) {
        this.facilityInformation = data;
        this.reviewDataInformation();
    }
    @Input() disabledZoom?: boolean;
    @Input() zoomCenterMarker: number = 6;
    @Input()
    set url(url: WebSite) {
        this.urlWebSite = url;
    }
    @Input()
    set initialIndex(index: number) {
        this.indexExpanded = index;
    }

    @ViewChild('map') mapElement: ElementRef<HTMLElement>;
    @ViewChildren('mapChildren') mapChildren: QueryList<ElementRef>;

    constructor(
        private media: MediaMatcher,
        private changeDetectorRef: ChangeDetectorRef,
        private currentFacilityService: CurrentFacilityService,
        private googleMapService: GoogleMapService
    ) {
        this.mobileQuery = this.media.matchMedia('(max-width: 767px)');
        this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
    }

    ngOnInit(): void {
        this.subscribeMobileSize();
        this.getCustomerServiceInfo();
    }

    reviewDataInformation(): void {
        this.isMapEnable = !!this.facilityInformation?.length && !!this.facilityInformation?.[0]?.position;

        if (this.isMapEnable) {
            this.initGoogleMaps();
        }
    }

    initGoogleMaps(): void {
        const markers = this.getMarkers();
        this.setMarkers(markers);
        this.setMapOptions();
    }

    setMarkers(newMarkers: AdvancedMarkerElementOptionsCustom[]): void {
        this.markers = newMarkers;
    }

    setMapOptions(options: google.maps.MapOptions = {}): void {
        const defaultMapsOptions = this.disabledZoom
            ? {
                  disableDefaultUI: true,
                  mapTypeControl: false,
                  zoom: this.zoom,
                  gestureHandling: 'none',
                  zoomControl: false,
              }
            : { mapTypeControl: false, zoom: this.zoom };
        const optionsMap = mergeDeep(defaultMapsOptions, options) as google.maps.MapOptions;

        this.mapOptions = optionsMap;
    }

    getMarkers(): AdvancedMarkerElementOptionsCustom[] {
        return this.facilityInformation?.reduce((result, info) => {
            if (info?.position) {
                result.push({
                    position: {
                        lat: info.position.lat,
                        lng: info.position.lng,
                    } as google.maps.LatLngLiteral,
                    icon: `assets/${this.getFacility()}/img/mark-map-icon.svg`,
                    gmpClickable: true,
                    title: info.address1,
                    gmpDraggable: false,
                });
            }

            return result;
        }, [] as AdvancedMarkerElementOptionsCustom[]);
    }

    subscribeMobileSize(): void {
        this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    }

    getFacility(): string {
        return this.currentFacilityService.getFacilitySource();
    }

    centerMarker(indexMarker: number, isExpanded: boolean): void {
        if (this.isMapEnable) {
            if (isExpanded) {
                this.setMapOptions({
                    center: this.markers[indexMarker].position,
                    zoom: this.zoomCenterMarker,
                });
                this.openAccordion(indexMarker);
            } else {
                from(this.googleMapService.createMarkers(this.markers))
                    .pipe(take(1))
                    .subscribe({
                        next: (markers) => {
                            const centerMap = this.googleMapService.getCenterMap(markers);
                            this.setMapOptions({
                                center: centerMap,
                                zoom: this.zoom,
                            });
                        },
                    });
            }
        }
    }

    setIndexExpanded(index: number): void {
        this.indexExpanded = index;
        this.openAccordion(index);
    }

    openAccordion(indexAccordion: number): void {
        let ele: ElementRef | undefined = this.mapChildren.find(
            (row) => row.nativeElement.id === 'accordion-header-' + indexAccordion
        );

        if (ele) {
            setTimeout(() =>
                ele?.nativeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest',
                })
            );
        }
    }

    getCustomerServiceInfo(): void {
        this.customerServiceInfo = this.currentFacilityService.getCustomerServiceInfo();
    }
}
